import React from "react";
import { RouteComponentProps } from "react-router";
import { Box, Typography } from "@material-ui/core";
import {
  PriceDropProtectionEnum,
  VIEWED_PRICE_DROP_DETAILS,
  Currency,
} from "redmond";
import {
  ActionButton,
  tripTypeText,
  PricingBreakdown,
  FlightPricingLineItem,
  ISummaryLineItem,
  PricingBreakdownAncillariesProps,
  ITotalLineItem,
  SanitizedHtml,
} from "halifax";
import { getFdaDisplayName } from "@capone/common";
import { useBusinessExperimentsById } from "@capone/experiments";
import clsx from "clsx";
import { PriceDropProtection as PriceDropProtectionDetails } from "../../../../book/components/PriceDropProtection";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import * as constants from "./constants";
import { AddOnPricingBreakdownConnectorProps } from "./container";
import "./styles.scss";

export interface IAddOnPricingBreakdownProps
  extends AddOnPricingBreakdownConnectorProps,
    RouteComponentProps {
  actionButtonProps?: {
    message: string | JSX.Element;
    disabled?: boolean;
    onContinue: () => void;
    onClickWhenDisabled?: () => void;
  };
  isMobile?: boolean;
  hasError?: boolean;
}

export const AddOnPricingBreakdown = (props: IAddOnPricingBreakdownProps) => {
  const {
    cfarOfferPrices,
    chfarOfferPrices,
    discountedCfarOffer,
    disruptionProtectionOfferPrices,
    actionButtonProps,
    isMobile,
    isAirOfferRedesignEnabled,
    hasError,
    cfarOfferCoverage,
    isCfarCoMerchEnabled,
    priceDropRefundType,
    isVITripSelected,
    fdaProductVariant,
  } = props;
  const freeFdaVariant = useBusinessExperimentsById("corp-free-fda")?.variant;
  const isFreeFdaEnabled =
    freeFdaVariant === "available" || freeFdaVariant === "available_phase2";

  const { selectedFareAmount, totalPrices, tripCategory, priceDropProtection } =
    getFareDetails(props);

  const flightType = constants.getPerTravelerCopy(tripTypeText[tripCategory]);

  const currencySymbol = totalPrices?.fiat.currencySymbol ?? "$";
  const currencyCode = totalPrices?.fiat.currencyCode ?? Currency.USD;

  return (
    <Box
      className={clsx("add-on-pricing-breakdown-root", { mobile: isMobile })}
    >
      <Box className="add-on-pricing-breakdown-container">
        <Box className="checkout-breakdown-header-section">
          <Typography className="header-copy" variant="h4">
            {constants.CHECKOUT_BREAKDOWN_HEADER}
          </Typography>
        </Box>
        {priceDropProtection &&
          priceDropProtection?.PriceDropProtection ===
            PriceDropProtectionEnum.IsEligible && (
            <Box className="pdp-section">
              <PriceDropProtectionDetails
                hideSubtitle
                priceDropProtection={priceDropProtection}
                onClick={() =>
                  trackEvent({
                    eventName: VIEWED_PRICE_DROP_DETAILS,
                    properties: {
                      page: "customize",
                      refund_type: priceDropRefundType,
                    },
                  })
                }
              />
            </Box>
          )}
        <Box className="pricing-breakdown-section">
          <PricingBreakdown
            pricingItems={buildPricingItems()}
            summaryItems={buildSummaryItems()}
            currencySymbol={currencySymbol}
            currencyCode={currencyCode}
            isVITripSelected={isVITripSelected}
            ancillaries={buildAncillariesProps()}
          />
        </Box>
        <Box className="total-price-section">
          <SanitizedHtml
            as={Typography}
            variant="body2"
            className="flight-type-copy"
            html={flightType}
          />
        </Box>
        {!!actionButtonProps && (
          <Box className="continue-cta-section">
            <ActionButton
              className="continue-cta"
              defaultStyle="h4r-primary"
              onClick={actionButtonProps.onContinue}
              onClickWhenDisabled={actionButtonProps.onClickWhenDisabled}
              message={actionButtonProps.message}
              disabled={actionButtonProps.disabled}
              dataTestId="flights-shop-breakdown-continue-button"
            />
          </Box>
        )}
        {isAirOfferRedesignEnabled && actionButtonProps?.disabled && (
          <div
            className={clsx("air-offer-redesign-validation-text", {
              hasError,
            })}
          >
            {constants.VALIDATION_TEXT}
          </div>
        )}
      </Box>
    </Box>
  );

  function buildPricingItems(): FlightPricingLineItem[] {
    return [
      {
        baseAmount: selectedFareAmount?.baseAmount?.fiat.value,
        taxesAndFees: selectedFareAmount?.taxAmount?.fiat.value,
      },
    ];
  }

  function buildAncillariesProps(): PricingBreakdownAncillariesProps {
    const props: PricingBreakdownAncillariesProps = {
      cfarFee: cfarOfferPrices?.fiat.value,
      cfarText: isCfarCoMerchEnabled
        ? `${constants.CFAR_COPY}, ${cfarOfferCoverage?.cashCoveragePercentage}% coverage`
        : undefined,
      origCfarFee: discountedCfarOffer?.originalPremiumAmount.fiat.value,
      chfarFee: chfarOfferPrices?.fiat.value,
      disruptionProtectionFee: disruptionProtectionOfferPrices?.fiat.value,
      isFreeFdaEnabled,
      fdaProductVariant,
      disruptionProtectionText: () =>
        getFdaDisplayName({
          isFreeFdaEnabled,
          productVariant: fdaProductVariant,
        }),
    };

    return props;
  }

  function buildSummaryItems(): ISummaryLineItem[] {
    return [
      {
        type: "total",
        fiatPrice: totalPrices?.fiat,
        rewardsPrice: totalPrices?.rewards,
      } as ITotalLineItem,
    ];
  }
};

function getFareDetails(props: IAddOnPricingBreakdownProps) {
  return {
    selectedFareAmount: props.selectedFare
      ? {
          baseAmount: props.selectedFare?.paxPricings?.[0]?.pricing?.baseAmount,
          taxAmount: props.selectedFare?.paxPricings?.[0]?.pricing?.taxAmount,
        }
      : undefined,
    tripCategory: props.tripCategory,
    totalPrices: props.totalPrices,
    priceDropProtection: props.prediction?.priceDropProtection,
  };
}
